import type { AppDispatch } from 'state';
import type { SelectValue } from 'antd/lib/select/index';
import type { SelectOption } from 'types/common/SelectOption';
import type { FlightCalendarsFiltersState } from 'routes/tourOperator/flightCache/flightCalendars/comp/flightCalendarsFilters';
import React, { useState, useEffect } from 'react';
import styled from 'styled-components/macro';
import { useSelector, useDispatch } from 'react-redux';
import { Select as AntdSelect } from 'antd';
import { FormattedMessage } from 'react-intl';
import {
  setAirportsFromValue,
  loadAndSetAirports,
  getAirportsFromValue,
  getAirportsFromOptions,
  getAirportsToValue,
  getAirportsToOptions,
  isFetchingAirportsFromInProgress,
  getOptionsForAirportsFrom,
} from 'routes/tourOperator/flightCache/flightCalendars/comp/flightCalendarsFilters';
import LabeledValue from 'comp/dataPresentation/labeledValue/LabeledValue';
import mediaQuery from 'styles/utils/mediaQuery';

function AirportsFrom(): JSX.Element {
  const [options, setOptions] = useState<SelectOption<string>[]>();
  const availableOptions = useSelector(getAirportsFromOptions);
  const value = useSelector(getAirportsFromValue);
  const loading = useSelector(isFetchingAirportsFromInProgress);
  const airportsToAvailableOptions = useSelector(getAirportsToOptions);
  const selectedAirportsTo = useSelector(getAirportsToValue);
  const dispatch = useDispatch() as AppDispatch;

  useEffect(() => {
    setOptions(dispatch(getOptionsForAirportsFrom));
  }, [availableOptions, airportsToAvailableOptions, selectedAirportsTo]);

  function handleOnChange(option: SelectValue) {
    if (option === undefined) {
      dispatch(setAirportsFromValue([]));
    } else {
      dispatch(setAirportsFromValue([option] as FlightCalendarsFiltersState['airportsFrom']));
    }
  }

  function handleOnFocus() {
    if (availableOptions === null) {
      dispatch(loadAndSetAirports());
    }
  }

  return (
    <LabeledValue label={{ id: 'airportsFrom' }}>
      <Select
        allowClear
        showSearch
        listHeight={640}
        loading={loading}
        placeholder={<FormattedMessage id='airportsFrom.placeholder' />}
        value={value || undefined}
        onFocus={handleOnFocus}
        onChange={handleOnChange}
      >
        {options?.map(({ value, label, data }) => (
          <Select.Option key={value} value={value}>
            {label}
            {data?.map((item) => (
              <div key={item} style={{ margin: '4px 4px 4px 8px', fontSize: '12px' }}>
                {item}
              </div>
            ))}
          </Select.Option>
        ))}
      </Select>
    </LabeledValue>
  );
}

export default AirportsFrom;

const Select = styled(AntdSelect)`
  width: 100%;

  ${mediaQuery.fromAndAbove.medium`
    width: 80px;
  `}
`;
